import { AsyncButton } from "aic";

interface AnnotationActionButtonsProps {
  isUnchanged: boolean;
  isFormValid: boolean;
  isClinicalLibraryAvailable: boolean;
  disabled?: boolean;
  onReset: () => void;
  onCancel: () => void;
  onSave: () => void;
  isPending?: boolean;
}

/**
 * Action buttons shown in the annotation modal footer.
 *
 * @param props - Destructured parameter object
 * @param props.isUnchanged - Whether the form has unsaved changes
 * @param props.isFormValid - Whether the form is valid for save
 * @param props.isClinicalLibraryAvailable - Whether the clinical library is available
 * @param props.disabled - Optional disabled flag
 * @param props.onReset - Reset handler
 * @param props.onCancel - Cancel handler
 * @param props.onSave - Save handler
 * @param props.isPending - Optional flag indicating save operation is in progress
 */
export const AnnotationActionButtons = ({
  isUnchanged,
  isFormValid,
  isClinicalLibraryAvailable,
  disabled = false,
  onReset,
  onCancel,
  onSave,
  isPending,
}: AnnotationActionButtonsProps): React.ReactElement => {
  const saveButtonText = "Save";

  return (
    <div className="d-flex justify-content-between w-100 mt-2 pt-2 border-top">
      <div>
        <AsyncButton
          id="annotation-reset-button"
          variant="outline-secondary"
          className="me-2"
          onClick={onReset}
          disabled={disabled || isUnchanged}
          size="sm"
        >
          Reset
        </AsyncButton>
      </div>
      <div>
        <AsyncButton
          id="annotation-cancel-button"
          variant="secondary"
          className="me-2"
          onClick={onCancel}
          size="sm"
        >
          Cancel
        </AsyncButton>
        <AsyncButton
          id="annotation-save-button"
          variant="primary"
          onClick={onSave}
          disabled={
            disabled || !isFormValid || !isClinicalLibraryAvailable || isUnchanged || isPending
          }
          size="sm"
          requestStatus={isPending ? { requesting: true, error: false } : undefined}
          pauseCoverText="Saving..."
        >
          {saveButtonText}
        </AsyncButton>
      </div>
    </div>
  );
};

AnnotationActionButtons.displayName = "AnnotationActionButtons";
